import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { 
  User, 
  Target, 
  Wrench, 
  Users, 
  Globe, 
  Building2, 
  CheckCircle2, 
  ArrowRight,
  Mail,
  Phone,
  MessageCircle,
  MapPin,
  Briefcase,
  TrendingUp,
  Award,
  Heart,
  Lightbulb,
  AlertTriangle,
  Store,
  Package,
  BookOpen,
  Bot,
  Linkedin,
  Quote
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const About = () => {
  const { i18n } = useTranslation();
  const isFr = i18n.language === 'fr';

  // Schema.org Organization + Person
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "GEOTS Consulting",
    "url": "https://geots.consulting",
    "logo": "https://geots.consulting/logo.png",
    "description": isFr 
      ? "Marketplace 100% spécialisée géotechnique et fondations spéciales entre France et Afrique francophone."
      : "100% specialized geotechnical and special foundations marketplace between France and French-speaking Africa.",
    "founder": {
      "@type": "Person",
      "name": "Hamdani Ali",
      "jobTitle": isFr ? "Technicien instrumentation forage & fondations spéciales" : "Drilling instrumentation & special foundations technician",
      "worksFor": {
        "@type": "Organization",
        "name": "SIM Métrologie"
      }
    },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Combs-la-Ville",
      "addressCountry": "FR"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+33782939383",
      "contactType": "customer service",
      "email": "contact@geots.consulting"
    },
    "areaServed": ["France", "Morocco", "Algeria", "Tunisia", "Senegal", "Ivory Coast"]
  };

  const stats = [
    { icon: Globe, value: "France & Afrique", label: isFr ? "Zone couverte" : "Coverage" },
    { icon: Store, value: "3+", label: isFr ? "Boutiques actives" : "Active shops" },
    { icon: Package, value: "70+", label: isFr ? "Produits disponibles" : "Products available" },
    { icon: BookOpen, value: "Guides", label: isFr ? "Experts publiés" : "Expert guides" },
    { icon: Bot, value: "IA", label: isFr ? "Comparateur intégré" : "Integrated comparator" },
    { icon: TrendingUp, value: "∞", label: isFr ? "Croissance continue" : "Continuous growth" },
  ];

  const differentiators = [
    { text: isFr ? "100% spécialisé géotechnique — pas un site généraliste" : "100% specialized geotechnical — not a generalist site" },
    { text: isFr ? "Bilingue & international — France + Maghreb + Afrique francophone" : "Bilingual & international — France + Maghreb + French-speaking Africa" },
    { text: isFr ? "Comparateur IA intégré — exclusivité GEOTS" : "Integrated AI comparator — GEOTS exclusive" },
    { text: isFr ? "Équipe du métier — des gens qui ont les mains dans le cambouis" : "Industry team — people with hands-on experience" },
    { text: isFr ? "Indépendance — SIM Métrologie est un fournisseur parmi d'autres" : "Independence — SIM Métrologie is one supplier among others" },
    { text: isFr ? "Engagement long terme — un projet de vie, pas opportuniste" : "Long-term commitment — a life project, not opportunistic" },
  ];

  const vision = [
    { icon: Award, text: isFr ? "LA référence métier — quand on pense géotech, on pense GEOTS" : "THE industry reference — when you think geotech, you think GEOTS" },
    { icon: Globe, text: isFr ? "LE pont Europe ⟷ Afrique — connecter les deux continents francophones" : "THE Europe ⟷ Africa bridge — connecting both French-speaking continents" },
    { icon: Briefcase, text: isFr ? "L'écosystème complet — matériel + talents + services + communauté" : "The complete ecosystem — equipment + talents + services + community" },
    { icon: Users, text: isFr ? "Une communauté soudée — pas une plateforme anonyme" : "A tight-knit community — not an anonymous platform" },
    { icon: BookOpen, text: isFr ? "Un savoir partagé — guides, formations, retours d'expérience" : "Shared knowledge — guides, training, feedback" },
  ];

  const solutions = [
    { icon: Wrench, title: isFr ? "Du matériel" : "Equipment", desc: isFr ? "Foreuses, pompes, centrales, instrumentation — achat, location, occasion" : "Drilling rigs, pumps, plants, instrumentation — purchase, rental, used" },
    { icon: Users, title: isFr ? "Des talents" : "Talents", desc: isFr ? "Opérateurs, chefs de chantier, mécaniciens, ingénieurs géotech" : "Operators, site managers, mechanics, geotech engineers" },
    { icon: Building2, title: isFr ? "Des services techniques" : "Technical services", desc: isFr ? "Maintenance, formation, étalonnage, audit, expertise" : "Maintenance, training, calibration, audit, expertise" },
    { icon: Bot, title: isFr ? "Comparateur IA" : "AI Comparator", desc: isFr ? "Analysez et comparez vos devis en quelques clics" : "Analyze and compare your quotes in a few clicks" },
    { icon: Store, title: isFr ? "Fournisseurs sérieux" : "Reliable suppliers", desc: isFr ? "Distributeurs officiels, fabricants, prestataires qualifiés" : "Official distributors, manufacturers, qualified providers" },
  ];

  return (
    <>
      <Helmet>
        <title>{isFr ? "À propos de GEOTS Consulting | Marketplace géotechnique" : "About GEOTS Consulting | Geotechnical Marketplace"}</title>
        <meta name="description" content={isFr 
          ? "Hamdani Ali, technicien spécialisé en instrumentation forage avec 15 ans d'expérience, présente GEOTS Consulting, la marketplace 100% géotechnique entre France et Afrique francophone."
          : "Hamdani Ali, drilling instrumentation specialist with 15 years of experience, presents GEOTS Consulting, the 100% geotechnical marketplace between France and French-speaking Africa."
        } />
        <link rel="canonical" href={`https://geots.consulting/${isFr ? 'a-propos' : 'about'}`} />
        <meta property="og:title" content={isFr ? "À propos de GEOTS Consulting" : "About GEOTS Consulting"} />
        <meta property="og:description" content={isFr 
          ? "La marketplace 100% spécialisée géotechnique et fondations spéciales."
          : "The 100% specialized geotechnical and special foundations marketplace."
        } />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://geots.consulting/${isFr ? 'a-propos' : 'about'}`} />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <header className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                {isFr ? "À propos de" : "About"} <span className="text-[#00D4FF]">GEOTS Consulting</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8">
                {isFr 
                  ? "La marketplace 100% spécialisée géotechnique et fondations spéciales — du technicien au directeur d'achats, des opérateurs aux fournisseurs."
                  : "The 100% specialized geotechnical and special foundations marketplace — from technician to purchasing director, from operators to suppliers."
                }
              </p>
              <div className="flex justify-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 inline-block">
                  <div className="w-24 h-24 bg-[#635BFF] rounded-full flex items-center justify-center mx-auto mb-4">
                    <User className="w-12 h-12 text-white" />
                  </div>
                  <p className="text-lg font-semibold">Hamdani Ali</p>
                  <p className="text-sm text-gray-300">{isFr ? "Fondateur" : "Founder"}</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Qui suis-je */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-[#0A2540] mb-8 flex items-center gap-3">
                <span className="text-4xl">👋</span>
                {isFr ? "Qui suis-je ?" : "Who am I?"}
              </h2>
              
              <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
                <p>
                  {isFr 
                    ? <>Je m'appelle <strong>Hamdani Ali</strong>, et je suis <strong>technicien spécialisé en instrumentation et systèmes embarqués pour la géotechnique et les fondations spéciales</strong>.</>
                    : <>My name is <strong>Hamdani Ali</strong>, and I am a <strong>technician specialized in instrumentation and embedded systems for geotechnics and special foundations</strong>.</>
                  }
                </p>
                
                <p>
                  {isFr 
                    ? <>Depuis plus de <strong>15 ans</strong>, je travaille sur le terrain avec les pros du forage, du sondage, de l'injection et du traitement de sols. J'installe, je configure et je maintiens des systèmes d'enregistrement de paramètres Jean Lutz sur les foreuses, je forme les opérateurs, je dépanne les chantiers, j'audite les parcs d'instrumentation.</>
                    : <>For over <strong>15 years</strong>, I have been working in the field with drilling, surveying, injection, and soil treatment professionals. I install, configure, and maintain Jean Lutz parameter recording systems on drilling rigs, train operators, troubleshoot sites, and audit instrumentation fleets.</>
                  }
                </p>
                
                <p className="text-[#635BFF] font-semibold text-xl">
                  {isFr 
                    ? "Je connais le métier de l'intérieur. Pas depuis un bureau ni un PowerPoint. Sur les chantiers, dans les ateliers, avec les conducteurs de foreuses."
                    : "I know the profession from the inside. Not from an office or PowerPoint. On job sites, in workshops, with drilling rig operators."
                  }
                </p>
                
                <p>
                  {isFr 
                    ? <>J'ai fondé <strong>SIM Métrologie</strong>, basée à Combs-la-Ville (77), distributeur officiel Jean Lutz en France. Et c'est sur le terrain, en parlant avec vous tous, que j'ai vu <strong>les mêmes problèmes revenir, encore et encore</strong>.</>
                    : <>I founded <strong>SIM Métrologie</strong>, based in Combs-la-Ville (77), official Jean Lutz distributor in France. And it was in the field, talking with all of you, that I saw <strong>the same problems coming back, again and again</strong>.</>
                  }
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Pourquoi GEOTS */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-[#0A2540] mb-8 flex items-center gap-3">
                <Target className="w-8 h-8 text-[#635BFF]" />
                {isFr ? "Pourquoi GEOTS Consulting ?" : "Why GEOTS Consulting?"}
              </h2>
              
              <p className="text-lg text-gray-700 mb-8">
                {isFr 
                  ? "Deux constats que personne ne résout vraiment :"
                  : "Two observations that nobody really solves:"
                }
              </p>

              {/* Problème 1 */}
              <div className="bg-white rounded-2xl border-l-4 border-l-red-500 p-6 mb-6 shadow-sm">
                <h3 className="text-xl font-bold text-red-600 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6" />
                  {isFr ? "Problème 1 : Trouver du matériel en bon état est un casse-tête" : "Problem 1: Finding equipment in good condition is a headache"}
                </h3>
                
                <div className="space-y-4 mb-6">
                  {[
                    isFr ? "\"On a besoin d'une foreuse pour 3 mois, mais celles qu'on trouve sont défaillantes ou hors de prix...\"" : "\"We need a drilling rig for 3 months, but the ones we find are faulty or overpriced...\"",
                    isFr ? "\"Notre fournisseur habituel n'a plus de stock, on perd 3 semaines à chercher...\"" : "\"Our usual supplier is out of stock, we're wasting 3 weeks searching...\"",
                    isFr ? "\"L'occasion qu'on a achetée se révèle être un gouffre financier...\"" : "\"The used equipment we bought turns out to be a money pit...\""
                  ].map((quote, i) => (
                    <blockquote key={i} className="border-l-2 border-gray-300 pl-4 italic text-gray-600">
                      {quote}
                    </blockquote>
                  ))}
                </div>
                
                <p className="text-gray-700 mb-4">{isFr ? "Le marché du matériel géotech existe, mais il est :" : "The geotech equipment market exists, but it is:"}</p>
                <ul className="grid grid-cols-2 gap-2">
                  {[
                    { emoji: "🌫️", text: isFr ? "Opaque : impossible de comparer" : "Opaque: impossible to compare" },
                    { emoji: "🐌", text: isFr ? "Lent : tout par téléphone et email" : "Slow: everything by phone and email" },
                    { emoji: "🎲", text: isFr ? "Risqué : achats sans inspection" : "Risky: purchases without inspection" },
                    { emoji: "🗺️", text: isFr ? "Fragmenté : pas de plateforme dédiée" : "Fragmented: no dedicated platform" },
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-700">
                      <span>{item.emoji}</span> {item.text}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Problème 2 */}
              <div className="bg-white rounded-2xl border-l-4 border-l-orange-500 p-6 shadow-sm">
                <h3 className="text-xl font-bold text-orange-600 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6" />
                  {isFr ? "Problème 2 : Le personnel qualifié manque cruellement" : "Problem 2: Qualified personnel are sorely lacking"}
                </h3>
                
                <p className="text-gray-700 mb-4">{isFr ? "Et c'est encore plus vrai au Maghreb :" : "And it's even more true in the Maghreb:"}</p>
                
                <div className="space-y-4 mb-6">
                  {[
                    isFr ? "\"Tu connais un opérateur foreuse expérimenté en Algérie ?\"" : "\"Do you know an experienced drilling operator in Algeria?\"",
                    isFr ? "\"On cherche un chef de chantier formé Jean Lutz au Maroc...\"" : "\"We're looking for a Jean Lutz trained site manager in Morocco...\"",
                    isFr ? "\"Notre BET cherche un ingénieur géotech qui parle français, on galère depuis 6 mois...\"" : "\"Our engineering firm is looking for a French-speaking geotech engineer, we've been struggling for 6 months...\""
                  ].map((quote, i) => (
                    <blockquote key={i} className="border-l-2 border-gray-300 pl-4 italic text-gray-600">
                      {quote}
                    </blockquote>
                  ))}
                </div>
                
                <p className="text-gray-700">
                  {isFr 
                    ? "Et pendant ce temps, des dizaines de pros qualifiés sont en recherche, mais sans plateforme pour se faire connaître dans notre niche."
                    : "Meanwhile, dozens of qualified professionals are looking for work, but without a platform to make themselves known in our niche."
                  }
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* La Solution */}
        <section className="py-16 bg-gradient-to-br from-[#635BFF] to-[#8B5CF6] text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
                <Lightbulb className="w-8 h-8" />
                {isFr ? "La solution : GEOTS Consulting" : "The solution: GEOTS Consulting"}
              </h2>
              <p className="text-xl text-white/80 mb-10">
                {isFr 
                  ? "J'ai créé GEOTS pour résoudre ces deux problèmes en même temps, à l'échelle France et Afrique francophone."
                  : "I created GEOTS to solve both problems at the same time, across France and French-speaking Africa."
                }
              </p>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {solutions.map((item, i) => (
                  <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-colors">
                    <item.icon className="w-10 h-10 mb-4 text-[#00D4FF]" />
                    <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                    <p className="text-white/80 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Maghreb et Afrique */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-[#0A2540] mb-8 flex items-center gap-3">
                <Globe className="w-8 h-8 text-[#635BFF]" />
                {isFr ? "Pourquoi le Maghreb et l'Afrique francophone ?" : "Why the Maghreb and French-speaking Africa?"}
              </h2>
              
              <p className="text-lg text-gray-700 mb-8">
                {isFr 
                  ? "Parce que c'est un marché gigantesque mais sous-équipé :"
                  : "Because it's a huge but under-equipped market:"
                }
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {[
                  { icon: "🏗️", text: isFr ? "Plus de 75 milliards de dollars de contrats d'infrastructure attribués en Afrique (2022-2024)" : "Over $75 billion in infrastructure contracts awarded in Africa (2022-2024)" },
                  { icon: "🇲🇦🇩🇿🇹🇳", text: isFr ? "Au Maghreb, les pros galèrent à accéder à du matériel européen sérieux" : "In the Maghreb, professionals struggle to access serious European equipment" },
                  { icon: "🇸🇳🇨🇮", text: isFr ? "En Afrique francophone, moins de 30% des firmes géotech ont accès aux équipements modernes" : "In French-speaking Africa, less than 30% of geotech firms have access to modern equipment" },
                  { icon: "🔗", text: isFr ? "Personne ne fait le pont efficacement entre l'Europe et l'Afrique" : "Nobody effectively bridges Europe and Africa" },
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-6 flex items-start gap-4">
                    <span className="text-3xl">{item.icon}</span>
                    <p className="text-gray-700">{item.text}</p>
                  </div>
                ))}
              </div>

              <div className="bg-[#00D4FF]/10 border border-[#00D4FF]/30 rounded-xl p-6">
                <p className="text-[#0A2540] font-semibold text-lg">
                  {isFr 
                    ? "Avec mon réseau au Maghreb et mon expertise européenne, GEOTS comble ce vide."
                    : "With my network in the Maghreb and my European expertise, GEOTS fills this gap."
                  }
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* L'équipe */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-[#0A2540] mb-8 flex items-center gap-3">
                <Users className="w-8 h-8 text-[#635BFF]" />
                {isFr ? "L'équipe" : "The Team"}
              </h2>
              
              <p className="text-lg text-gray-700 mb-8">
                {isFr 
                  ? "GEOTS n'est pas un projet solo. Je suis entouré d'une équipe de 2-3 personnes : techniciens du métier et développeurs spécialisés."
                  : "GEOTS is not a solo project. I am surrounded by a team of 2-3 people: industry technicians and specialized developers."
                }
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { icon: Wrench, title: isFr ? "Compétence technique" : "Technical expertise", desc: isFr ? "On connaît votre métier, votre vocabulaire, vos contraintes" : "We know your profession, your vocabulary, your constraints" },
                  { icon: Building2, title: isFr ? "Compétence digitale" : "Digital expertise", desc: isFr ? "Un outil moderne, rapide, qui marche sur mobile" : "A modern, fast tool that works on mobile" },
                  { icon: Target, title: isFr ? "Compétence terrain" : "Field expertise", desc: isFr ? "On ne réinvente pas la roue, on résout vos vrais problèmes" : "We don't reinvent the wheel, we solve your real problems" },
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-xl p-6 text-center shadow-sm">
                    <div className="bg-[#635BFF]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                      <item.icon className="w-8 h-8 text-[#635BFF]" />
                    </div>
                    <h3 className="font-bold text-[#0A2540] mb-2">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Ce qui nous différencie */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-[#0A2540] mb-8 flex items-center gap-3">
                <Award className="w-8 h-8 text-[#635BFF]" />
                {isFr ? "Ce qui nous différencie" : "What sets us apart"}
              </h2>
              
              <p className="text-lg text-gray-700 mb-8">
                {isFr ? "Pourquoi GEOTS et pas une autre plateforme ?" : "Why GEOTS and not another platform?"}
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                {differentiators.map((item, i) => (
                  <div key={i} className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Vision 5 ans */}
        <section className="py-16 bg-[#0A2540] text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
                <Target className="w-8 h-8 text-[#00D4FF]" />
                {isFr ? "Notre vision à 5 ans" : "Our 5-year vision"}
              </h2>
              <p className="text-xl text-gray-300 mb-10">
                {isFr 
                  ? "Devenir LA référence francophone en géotechnique et fondations spéciales."
                  : "Become THE French-speaking reference in geotechnics and special foundations."
                }
              </p>

              <div className="space-y-4">
                {vision.map((item, i) => (
                  <div key={i} className="flex items-start gap-4 bg-white/5 rounded-xl p-5">
                    <div className="bg-[#635BFF] p-3 rounded-lg">
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                    <p className="text-gray-200 text-lg">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Quelques chiffres */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-[#0A2540] mb-10 text-center">
                {isFr ? "Quelques chiffres" : "Some numbers"}
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {stats.map((stat, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 text-center shadow-sm">
                    <stat.icon className="w-8 h-8 text-[#635BFF] mx-auto mb-2" />
                    <p className="text-2xl font-bold text-[#0A2540]">{stat.value}</p>
                    <p className="text-sm text-gray-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Triple */}
        <section className="py-16 bg-gradient-to-br from-[#635BFF] to-[#8B5CF6] text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-4 text-center">
                {isFr ? "Vous êtes professionnel ? Rejoignez-nous" : "Are you a professional? Join us"}
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6 mt-10">
                {/* Fournisseur */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                  <Store className="w-12 h-12 mx-auto mb-4 text-[#00D4FF]" />
                  <h3 className="text-xl font-bold mb-2">{isFr ? "Fournisseur" : "Supplier"}</h3>
                  <p className="text-white/80 text-sm mb-4">
                    {isFr 
                      ? "Les 100 premiers fournisseurs bénéficient de 6 mois Premium offerts."
                      : "The first 100 suppliers get 6 months Premium free."
                    }
                  </p>
                  <Link to="/register">
                    <Button className="bg-white text-[#635BFF] hover:bg-gray-100 w-full">
                      {isFr ? "Devenir fournisseur" : "Become a supplier"}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>

                {/* Acheteur */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                  <Package className="w-12 h-12 mx-auto mb-4 text-[#00D4FF]" />
                  <h3 className="text-xl font-bold mb-2">{isFr ? "Acheteur" : "Buyer"}</h3>
                  <p className="text-white/80 text-sm mb-4">
                    {isFr 
                      ? "Inscription gratuite, catalogue, comparateur IA, devis multiples."
                      : "Free registration, catalog, AI comparator, multiple quotes."
                    }
                  </p>
                  <Link to="/register">
                    <Button className="bg-white text-[#635BFF] hover:bg-gray-100 w-full">
                      {isFr ? "Créer mon compte" : "Create my account"}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>

                {/* Talent */}
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
                  <Users className="w-12 h-12 mx-auto mb-4 text-[#00D4FF]" />
                  <h3 className="text-xl font-bold mb-2">{isFr ? "Talent géotech" : "Geotech Talent"}</h3>
                  <p className="text-white/80 text-sm mb-4">
                    {isFr 
                      ? "Présentez votre profil aux entreprises BTP qui recrutent."
                      : "Present your profile to recruiting construction companies."
                    }
                  </p>
                  <Link to="/talents">
                    <Button className="bg-white text-[#635BFF] hover:bg-gray-100 w-full">
                      {isFr ? "Publier mon profil" : "Publish my profile"}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-[#0A2540] mb-8 text-center">
                {isFr ? "Nous contacter" : "Contact us"}
              </h2>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: Mail, label: "Email", value: "contact@geots.consulting", href: "mailto:contact@geots.consulting" },
                  { icon: MessageCircle, label: "WhatsApp", value: "+33 7 82 93 93 83", href: "https://wa.me/33782939383" },
                  { icon: Phone, label: isFr ? "Téléphone" : "Phone", value: "+33 7 82 93 93 83", href: "tel:+33782939383" },
                  { icon: MapPin, label: isFr ? "Adresse" : "Address", value: "Combs-la-Ville (77), France", href: null },
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-6 text-center">
                    <item.icon className="w-8 h-8 text-[#635BFF] mx-auto mb-3" />
                    <p className="text-sm text-gray-500 mb-1">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} className="text-[#0A2540] font-medium hover:text-[#635BFF] transition-colors">
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-[#0A2540] font-medium">{item.value}</p>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-8 text-center">
                <p className="text-gray-600 mb-4">
                  {isFr 
                    ? "Une question ? Une suggestion ? Je suis personnellement disponible pour échanger avec vous."
                    : "A question? A suggestion? I am personally available to discuss with you."
                  }
                </p>
                <a 
                  href="https://www.linkedin.com/in/hamdani-ali" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#635BFF] font-semibold hover:underline"
                >
                  <Linkedin className="w-5 h-5" />
                  {isFr ? "Connectons-nous sur LinkedIn" : "Let's connect on LinkedIn"}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Mot du fondateur */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold text-[#0A2540] mb-8 text-center flex items-center justify-center gap-3">
                <Quote className="w-8 h-8 text-[#635BFF]" />
                {isFr ? "Le mot du fondateur" : "A word from the founder"}
              </h2>

              <div className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-l-[#635BFF]">
                <blockquote className="text-xl italic text-gray-700 mb-6">
                  {isFr 
                    ? "\"En 15 ans dans la géotechnique, j'ai appris qu'aucun projet ne réussit sans bons partenaires et sans personnel qualifié. GEOTS, c'est ma manière de remettre l'humain au cœur d'un métier de plus en plus exigeant. Que vous soyez en France ou au Maghreb, sur le terrain ou au bureau, vous êtes ici chez vous.\""
                    : "\"In 15 years in geotechnics, I learned that no project succeeds without good partners and qualified personnel. GEOTS is my way of putting people back at the heart of an increasingly demanding profession. Whether you're in France or the Maghreb, in the field or in the office, you are at home here.\""
                  }
                </blockquote>
                
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-[#635BFF] rounded-full flex items-center justify-center">
                    <User className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-[#0A2540]">Hamdani Ali</p>
                    <p className="text-sm text-gray-500">{isFr ? "Fondateur de GEOTS Consulting" : "Founder of GEOTS Consulting"}</p>
                    <p className="text-sm text-gray-500">{isFr ? "Technicien instrumentation forage — 15 ans d'expérience" : "Drilling instrumentation technician — 15 years of experience"}</p>
                  </div>
                </div>
              </div>

              <p className="text-center text-gray-500 text-sm mt-8">
                {isFr ? "Page mise à jour le 11 mai 2026" : "Page updated on May 11, 2026"}
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;
